import React, { PropsWithChildren, ReactNode } from 'react'
import { TbChevronRight } from 'react-icons/tb'
import { Link, LinkProps } from 'react-router-dom'

import { cx, sva } from 'styled-system/css'
import { HStack, HstackProps } from 'styled-system/jsx'
import { Icon } from 'ui-kit/icon'
import { Link as UiLink, LinkProps as UiLinkProps } from 'ui-kit/link'
import { Text, TextProps } from 'ui-kit/text'

const breadcrumbRecipe = sva({
  className: 'breadcrumb',
  slots: ['root', 'item', 'text', 'link', 'iconSlot', 'rightSlot', 'separator'],
  base: {
    root: {
      textStyle: 'md',
      gap: 2,
    },
    item: {
      gap: 1,
    },
    link: {
      gap: 1,
      display: 'inline-flex',
      alignItems: 'center',
      _hover: {
        colorPalette: 'primary',
      },
      '& svg, & img': {
        width: 4,
        height: 4,
      },
    },
    text: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      color: 'fg.muted',
      '& svg, & img': {
        width: 4,
        height: 4,
      },
    },
    separator: {
      '&:last-child': {
        display: 'none',
      },
    },
  },
})

const breadcrumbRecipeClassnames = breadcrumbRecipe()

function BreadcrumbRoot({ children, className, ...props }: HstackProps) {
  return (
    <HStack
      className={cx(breadcrumbRecipeClassnames.root, className)}
      {...props}
    >
      {children}
    </HStack>
  )
}

type SlotsProps = {
  leftSlot?: ReactNode
  rightSlot?: ReactNode
}

function BreadcrumbLink({
  children,
  to,
  className,
  leftSlot,
  rightSlot,
  ...props
}: UiLinkProps & Pick<LinkProps, 'to'> & SlotsProps) {
  return (
    <>
      <HStack className={breadcrumbRecipeClassnames.item}>
        <Slot>{leftSlot}</Slot>
        <UiLink
          asChild
          className={cx(breadcrumbRecipeClassnames.link, className)}
          {...props}
        >
          <Link to={to}>{children}</Link>
        </UiLink>
        <Slot>{rightSlot}</Slot>
      </HStack>
      <Icon size={'xs'} className={breadcrumbRecipeClassnames.separator}>
        <TbChevronRight />
      </Icon>
    </>
  )
}

function BreadcrumbText({
  children,
  className,
  leftSlot,
  rightSlot,
  ...props
}: TextProps & SlotsProps) {
  return (
    <>
      <HStack className={breadcrumbRecipeClassnames.item}>
        <Slot>{leftSlot}</Slot>
        <Text
          as='span'
          className={cx(breadcrumbRecipeClassnames.text, className)}
          {...props}
        >
          {children}
        </Text>
        <Slot>{rightSlot}</Slot>
      </HStack>
      <Icon size={'xs'} className={breadcrumbRecipeClassnames.separator}>
        <TbChevronRight />
      </Icon>
    </>
  )
}

function Slot({ children }: PropsWithChildren) {
  if (!children) {
    return null
  }
  return <>{children}</>
}

const Breadcrumb = {
  Root: BreadcrumbRoot,
  Link: BreadcrumbLink,
  Text: BreadcrumbText,
}

export { Breadcrumb }
